import { useEffect, useMemo, useRef } from 'react'

// art/Packet — the dot that travels a diagram edge.
//
// Replaces `<circle><animateMotion repeatCount="indefinite" path=…/></circle>`,
// which shipped at four sites and is a blind spot twice over: SMIL runs
// forever, and the global reduced-motion CSS reset does not stop it. Measured
// on #/story, where one such diagram sat inside an INERT exhibit: 120 forced
// layouts per second at rest, unchanged under `prefers-reduced-motion: reduce`
// — the figure docs/cloudsmith-teardown.md §10.3 records as cloudsmith.com's
// headline failure.
//
// WHY NOT `offset-path`, WHICH IS THE OBVIOUS ANSWER AND WAS THE FIRST TRY:
// it is not compositor-friendly in Blink. Measured on an isolated page, six
// dots on one curve:
//
//     offset-distance     120 layouts/s   2.7% cpu
//     transform:translate   0 layouts/s   0.1% cpu     ← compositor
//     cx (what SMIL does) 120 layouts/s   2.5% cpu
//
// So `offset-path` buys the reduced-motion fix and nothing else; it costs
// exactly what it replaced. Only `transform` is free. Hence: sample the path
// once, hand the points to the Web Animations API as transform keyframes, and
// let the compositor run it. Still zero JS per frame — the sampling happens
// once at mount.

const SAMPLES = 48

/** One hidden path, reused: getTotalLength/getPointAtLength need an element,
 *  and Safari has historically wanted it in the document rather than detached. */
let probe: SVGPathElement | null = null
function pathProbe(): SVGPathElement | null {
  if (typeof document === 'undefined') return null
  if (probe) return probe
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('width', '0')
  svg.setAttribute('height', '0')
  svg.setAttribute('aria-hidden', 'true')
  svg.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none'
  probe = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  svg.appendChild(probe)
  document.body.appendChild(svg)
  return probe
}

function samplePath(d: string, n = SAMPLES): { x: number; y: number }[] {
  const p = pathProbe()
  if (!p) return []
  try {
    p.setAttribute('d', d)
    const len = p.getTotalLength()
    if (!len || !Number.isFinite(len)) return []
    const out: { x: number; y: number }[] = []
    for (let i = 0; i <= n; i++) {
      const pt = p.getPointAtLength((len * i) / n)
      out.push({ x: pt.x, y: pt.y })
    }
    return out
  } catch {
    return [] // a malformed `d` must not take the diagram down with it
  }
}

export function Packet({
  d,
  r = 3,
  dur,
  className = '',
  still = false,
}: {
  /** The edge path this dot rides. */
  d: string
  r?: number
  /** Seconds for one traversal. */
  dur: number
  className?: string
  /** Render the dot parked — for exhibits, where the diagram is a picture. */
  still?: boolean
}) {
  const ref = useRef<SVGCircleElement>(null)
  const pts = useMemo(() => samplePath(d), [d])
  const start = pts[0] ?? { x: 0, y: 0 }

  useEffect(() => {
    const el = ref.current
    if (!el || still || pts.length < 2) return
    // Only while the diagram is on screen. The animation cannot be composited
    // inside these diagrams (an ancestor carries `transform: scale()` for
    // fit-to-container), so every visible packet costs real layout — the least
    // we can do is stop paying for the ones nobody is looking at. Same gate,
    // and the same reason, as art/LottieArt.tsx.
    let anim: Animation | null = null
    const start = () => {
      if (anim) return
      // Decided here rather than in CSS because the animation is created in
      // JS: a media query cannot cancel a WAAPI animation.
      if (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches) return
      if (typeof el.animate !== 'function') return
      anim = el.animate(
        pts.map((p) => ({ transform: `translate(${p.x}px, ${p.y}px)` })),
        { duration: dur * 1000, iterations: Infinity, easing: 'linear' },
      )
    }
    const stop = () => {
      anim?.cancel()
      anim = null
    }
    if (typeof IntersectionObserver !== 'function') {
      start()
      return stop
    }
    const io = new IntersectionObserver((obs) => (obs.some((o) => o.isIntersecting) ? start() : stop()))
    io.observe(el)
    return () => {
      io.disconnect()
      stop()
    }
  }, [pts, dur, still])

  return (
    <circle
      ref={ref}
      r={r}
      cx={0}
      cy={0}
      className={`art-packet ${className}`}
      // The parked pose, and the pose a reduced-motion reader keeps: at the
      // head of its edge, which still reads as "this edge carries work".
      style={{ transform: `translate(${start.x}px, ${start.y}px)` }}
    />
  )
}
